import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CheckCircle, XCircle, RotateCcw, Search, Zap, AlertCircle, Loader2 } from 'lucide-react';
import { TestResult } from '../types';
import { TestReviewFeedback, testReviewService } from '../services/testReviewService';
import AuthService from '../services/auth';
import './TestReviewInterface.css';

interface AgentConfig {
  id: string;
  displayName: string;
  color: string;
  icon: string;
  hasTestResults: boolean;
}

interface TestReviewInterfaceProps {
  testResults: TestResult[];
  testFileName: string;
  agentConfig: AgentConfig;
  onClose: () => void;
  onComplete: () => void;
}

type ReviewPhase = 'validating_positives' | 'validating_negatives' | 'knowledge_base' | 'complete';

interface ReviewCard {
  result: TestResult;
  index: number;
}

const TestReviewInterface: React.FC<TestReviewInterfaceProps> = ({
  testResults,
  testFileName,
  agentConfig,
  onClose,
  onComplete
}) => {
  const authService = AuthService.getInstance();
  const user = authService.getCachedUser();

  // Function to process generated answer and remove sources section
  const processGeneratedAnswer = (answer: string): string => {
    // Remove everything from various sources header formats onward
    const sourcesRegex = /(\*\*Sources:\*\*|#{1,3}\s*Sources:?)[\s\S]*$/mi;
    let processedAnswer = answer.replace(sourcesRegex, '').trim();
    
    // Remove excessive newlines and normalize spacing
    processedAnswer = processedAnswer.replace(/\n{3,}/g, '\n\n');
    
    // Add redaction notice at the end
    processedAnswer += '\n\n*(source information redacted, accessible below)*';
    
    return processedAnswer;
  };
  const userEmail = user ? authService.getUserEmail(user) : '';

  // Review state
  const [currentPhase, setCurrentPhase] = useState<ReviewPhase>('validating_positives');
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  // Progress tracking
  const [positivesCompleted, setPositivesCompleted] = useState(0);
  const [negativesCompleted, setNegativesCompleted] = useState(0);
  const [kbCompleted, setKbCompleted] = useState(0);

  // Prepare review cards for each phase
  const reviewCards = useMemo(() => {
    // Phase 1: 3 random satisfactory results
    const satisfactoryResults = testResults.filter(r => r.is_satisfactory);
    const randomPositives = satisfactoryResults
      .sort(() => Math.random() - 0.5)
      .slice(0, 3)
      .map((result, index) => ({ result, index }));

    // Phase 2: 3 random unsatisfactory results that are not KB issues
    const unsatisfactoryNonKB = testResults.filter(r => 
      !r.is_satisfactory && 
      r.source_status !== 'insufficient_knowledge_base' && 
      r.source_status !== 'partial_context_available'
    );
    const randomNegatives = unsatisfactoryNonKB
      .sort(() => Math.random() - 0.5)
      .slice(0, 3)
      .map((result, index) => ({ result, index }));

    // Phase 3: All KB issues
    const kbIssues = testResults.filter(r => 
      !r.is_satisfactory && 
      (r.source_status === 'insufficient_knowledge_base' || 
       r.source_status === 'partial_context_available')
    ).map((result, index) => ({ result, index }));

    return {
      validating_positives: randomPositives,
      validating_negatives: randomNegatives,
      knowledge_base: kbIssues
    };
  }, [testResults]);

  // Load existing progress
  useEffect(() => {
    const loadProgress = async () => {
      if (!userEmail || !testFileName) return;

      try {
        const feedback = await testReviewService.getUserFeedback(testFileName, userEmail);
        const totalKbIssues = reviewCards.knowledge_base.length;
        const progress = testReviewService.calculateProgressFromFeedback(feedback, totalKbIssues);

        setPositivesCompleted(progress.validating_positives_completed);
        setNegativesCompleted(progress.validating_negatives_completed);
        setKbCompleted(progress.knowledge_base_completed);

        // Determine current phase and position
        if (progress.validating_positives_completed < 3) {
          setCurrentPhase('validating_positives');
          setCurrentCardIndex(progress.validating_positives_completed);
        } else if (progress.validating_negatives_completed < 3) {
          setCurrentPhase('validating_negatives');
          setCurrentCardIndex(progress.validating_negatives_completed);
        } else if (progress.knowledge_base_completed < totalKbIssues) {
          setCurrentPhase('knowledge_base');
          setCurrentCardIndex(progress.knowledge_base_completed);
        } else {
          setCurrentPhase('complete');
        }
      } catch (error) {
        console.error('Failed to load progress:', error);
      }
    };

    loadProgress();
  }, [userEmail, testFileName, reviewCards.knowledge_base.length]);

  const getCurrentCards = (): ReviewCard[] => {
    if (currentPhase === 'complete') return [];
    return reviewCards[currentPhase] || [];
  };

  const getCurrentCard = (): ReviewCard | null => {
    const cards = getCurrentCards();
    return cards[currentCardIndex] || null;
  };

  const getCurrentQuestionNumber = (): number => {
    if (currentPhase === 'validating_positives') {
      return currentCardIndex + 1;
    } else if (currentPhase === 'validating_negatives') {
      return positivesCompleted + currentCardIndex + 1;
    } else if (currentPhase === 'knowledge_base') {
      return positivesCompleted + negativesCompleted + currentCardIndex + 1;
    }
    return 1;
  };

  const getPhaseTitle = (): string => {
    const completed = currentPhase === 'validating_positives' ? positivesCompleted :
                     currentPhase === 'validating_negatives' ? negativesCompleted : kbCompleted;
    const total = currentPhase === 'knowledge_base' ? reviewCards.knowledge_base.length : 3;

    switch (currentPhase) {
      case 'validating_positives':
        return `Phase 1: Validating "Satisfactory" Responses (${completed}/3)`;
      case 'validating_negatives':
        return `Phase 2: Validating "Dissatisfactory" Responses (${completed}/3)`;
      case 'knowledge_base':
        return `Phase 3: Evaluating Knowledge Base Issues (${completed}/${total})`;
      case 'complete':
        return 'Review Complete';
      default:
        return '';
    }
  };

  const getTotalQuestions = (): number => {
    return 3 + 3 + reviewCards.knowledge_base.length; // 3 positives + 3 negatives + KB issues
  };

  const getCurrentProgress = (): number => {
    return positivesCompleted + negativesCompleted + kbCompleted;
  };

  const getProgressPercentage = (): number => {
    const total = getTotalQuestions();
    const current = getCurrentProgress();
    return total > 0 ? (current / total) * 100 : 0;
  };

  const restartTestReview = async () => {
    if (!userEmail) return;
    
    const confirmRestart = window.confirm(
      'Are you sure you want to restart the test review? This will delete all your previous feedback for this test file and start over.'
    );
    
    if (!confirmRestart) return;
    
    try {
      await testReviewService.deleteUserFeedback(testFileName, userEmail);
      
      // Reset all state
      setCurrentPhase('validating_positives');
      setCurrentCardIndex(0);
      setPositivesCompleted(0);
      setNegativesCompleted(0);
      setKbCompleted(0);
      setNotes('');
      setIsExpanded(false);
      
      alert('Test review has been restarted successfully.');
    } catch (error) {
      console.error('Failed to restart test review:', error);
      alert('Failed to restart test review. Please try again.');
    }
  };

  const submitFeedback = async (feedback: string) => {
    if (!userEmail || !testFileName || currentPhase === 'complete') return;

    const currentCard = getCurrentCard();
    if (!currentCard) return;

    setIsSubmitting(true);

    try {
      const feedbackData: TestReviewFeedback = {
        test_file: testFileName,
        evaluator: userEmail,
        timestamp: new Date().toISOString(),
        question: currentCard.result.question,
        generated_answer: currentCard.result.generated_answer,
        category: currentPhase as 'validating_positives' | 'validating_negatives' | 'knowledge_base',
        feedback: feedback as any,
        notes: notes.trim()
      };

      await testReviewService.submitFeedback(feedbackData);

      // Update progress counts
      if (currentPhase === 'validating_positives') {
        setPositivesCompleted(prev => prev + 1);
      } else if (currentPhase === 'validating_negatives') {
        setNegativesCompleted(prev => prev + 1);
      } else if (currentPhase === 'knowledge_base') {
        setKbCompleted(prev => prev + 1);
      }

      // Move to next card or phase
      moveToNext();
      setNotes('');
      setIsExpanded(false);

    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const moveToNext = () => {
    const currentCards = getCurrentCards();
    
    if (currentCardIndex + 1 < currentCards.length) {
      // Move to next card in current phase
      setCurrentCardIndex(currentCardIndex + 1);
    } else {
      // Move to next phase
      if (currentPhase === 'validating_positives') {
        setCurrentPhase('validating_negatives');
        setCurrentCardIndex(0);
      } else if (currentPhase === 'validating_negatives') {
        setCurrentPhase('knowledge_base');
        setCurrentCardIndex(0);
      } else if (currentPhase === 'knowledge_base') {
        setCurrentPhase('complete');
        onComplete();
      }
    }
  };

  const renderPhaseButtons = () => {
    if (currentPhase === 'validating_positives') {
      return (
        <div className="feedback-buttons">
          <button
            className="feedback-btn agree"
            onClick={() => submitFeedback('agree')}
            disabled={isSubmitting}
          >
            <CheckCircle className="h-4 w-4 inline mr-1" />
            Satisfactory
          </button>
          <button
            className="feedback-btn disagree"
            onClick={() => submitFeedback('disagree')}
            disabled={isSubmitting}
          >
            <XCircle className="h-4 w-4 inline mr-1" />
            Dissatisfactory
          </button>
        </div>
      );
    } else if (currentPhase === 'validating_negatives') {
      return (
        <div className="feedback-buttons">
          <button
            className="feedback-btn agree"
            onClick={() => submitFeedback('agree')}
            disabled={isSubmitting}
          >
            <CheckCircle className="h-4 w-4 inline mr-1" />
            Satisfactory
          </button>
          <button
            className="feedback-btn disagree"
            onClick={() => submitFeedback('disagree')}
            disabled={isSubmitting}
          >
            <XCircle className="h-4 w-4 inline mr-1" />
            Dissatisfactory
          </button>
        </div>
      );
    } else if (currentPhase === 'knowledge_base') {
      return (
        <div className="feedback-buttons kb-buttons">
          <button
            className="feedback-btn confirm-kb"
            onClick={() => submitFeedback('confirm_kb_issue')}
            disabled={isSubmitting}
          >
            <Search className="h-4 w-4 inline mr-1" />
            Confirm Knowledge Base Issue
          </button>
          <button
            className="feedback-btn not-kb"
            onClick={() => submitFeedback('not_kb_issue')}
            disabled={isSubmitting}
          >
            <Zap className="h-4 w-4 inline mr-1" />
            Not a Knowledge Base Issue
          </button>
          <button
            className="feedback-btn satisfactory"
            onClick={() => submitFeedback('answer_satisfactory')}
            disabled={isSubmitting}
          >
            <AlertCircle className="h-4 w-4 inline mr-1" />
            Answer is Satisfactory
          </button>
        </div>
      );
    }
    return null;
  };

  if (currentPhase === 'complete') {
    return (
      <div className="test-review-interface">
        <div className="review-complete">
          <h2>🎉 Review Complete!</h2>
          <p>You have successfully completed the review for this test file.</p>
                      <div className="completion-stats">
              <div className="stat">
                <span className="stat-value">{positivesCompleted}</span>
                <span className="stat-label">Satisfactory Validated</span>
              </div>
              <div className="stat">
                <span className="stat-value">{negativesCompleted}</span>
                <span className="stat-label">Dissatisfactory Validated</span>
              </div>
              <div className="stat">
                <span className="stat-value">{kbCompleted}</span>
                <span className="stat-label">KB Issues Evaluated</span>
              </div>
            </div>
          
          <div className="completion-actions">
            <button 
              className="restart-btn"
              onClick={restartTestReview}
              disabled={isSubmitting}
            >
              <RotateCcw className="h-4 w-4 inline mr-1" />
              Restart Test Review
            </button>
            <button className="close-btn" onClick={onClose}>
              Return to Test Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentCard = getCurrentCard();
  if (!currentCard) {
    return (
      <div className="test-review-interface">
        <div className="no-cards">
          <h2>No Cards Available</h2>
          <p>No test results available for review in the current phase.</p>
          <button className="close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="test-review-interface">
      <div className="review-header">
        <div className="phase-info">
          <h2>{getPhaseTitle()}</h2>
          <div className="progress-info">
            <span className="progress-text">
              Question {getCurrentProgress() + 1} of {getTotalQuestions()}
            </span>
          </div>
        </div>
        <button className="close-btn" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="full-width-progress">
        <div 
          className="progress-fill" 
          style={{ 
            width: `${getProgressPercentage()}%` 
          }}
        />
      </div>

      <div className="review-card">
        <div className="review-question">
          <h4>Question #{getCurrentQuestionNumber()}: <span className="question-text">{currentCard.result.question}</span></h4>
        </div>

        <div className="phase-description">
          {currentPhase === 'validating_positives' && (
            <div className="phase-desc phase-1" style={{ '--agent-color': agentConfig.color } as React.CSSProperties}>
              🔍 This response was labeled <strong>satisfactory</strong>. What do you think?
            </div>
          )}
          {currentPhase === 'validating_negatives' && (
            <div className="phase-desc phase-2">
              🔍 This response was labeled <strong>dissatisfactory</strong>. What do you think?
            </div>
          )}
          {currentPhase === 'knowledge_base' && (
            <div className="phase-desc phase-3">
              🔍 This response was labeled as dissatisfactory due to <strong>lacking necessary knowledge base information</strong>. What do you think?
            </div>
          )}
        </div>



        <div className="card-section">
          <h5>Ideal Answer:</h5>
          <div className="ideal-answer">
            <p>{currentCard.result.ideal_answer}</p>
          </div>
        </div>

        <div className="card-section">
          <h5>Generated Answer:</h5>
          <div className="generated-answer-scroll">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {processGeneratedAnswer(currentCard.result.generated_answer)}
            </ReactMarkdown>
          </div>
        </div>

        <div className="card-section">
          <div className="additional-context-section">
            <button 
              className="additional-context-btn"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <span>{isExpanded ? '▼' : '▶'}</span>
              Additional Context
            </button>
            {isExpanded && (
              <div className="additional-context-expanded">
                <div className="context-item">
                  <h6>Scores:</h6>
                  <div className="card-scores">
                    <div className="score-item">
                      <span>Completeness</span>
                      <span className="score">{currentCard.result.completeness}/5</span>
                    </div>
                    <div className="score-item">
                      <span>Correctness</span>
                      <span className="score">{currentCard.result.correctness}/5</span>
                    </div>
                    <div className="score-item">
                      <span>Conciseness</span>
                      <span className="score">{currentCard.result.conciseness}/5</span>
                    </div>
                  </div>
                </div>

                <div className="context-item">
                  <h6>Analysis:</h6>
                  <p>{currentCard.result.analysis}</p>
                </div>

                <div className="context-item">
                  <h6>Sources:</h6>
                  <p>{currentCard.result.sources}</p>
                </div>

                <div className="context-item">
                  <h6>Source Content:</h6>
                  {currentCard.result.source_content ? (
                    <div className="source-content-scroll">
                      {currentCard.result.source_content}
                    </div>
                  ) : (
                    <p className="no-source-content">No source content available</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {renderPhaseButtons()}

        <div className="notes-section">
          <div className="notes-inline">
            <span className="notes-label">Notes (optional):</span>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional comments or notes..."
              rows={2}
              className="notes-textarea-inline"
            />
          </div>
        </div>
      </div>

      <div className="restart-section">
        <button 
          className="restart-btn"
          onClick={restartTestReview}
          disabled={isSubmitting}
        >
          <RotateCcw className="h-4 w-4 inline mr-1" />
          Restart Test Review
        </button>
      </div>

      {isSubmitting && (
        <div className="submitting-overlay">
          <div className="submitting-spinner">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </div>
      )}
    </div>
  );
};



export default TestReviewInterface;